/* eslint-disable prefer-destructuring */
import { Box, Divider, Group, Input, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useApiDeleteAllowedDomain,
	useApiListAllowedDomains,
	useApiListIamRoles,
	useVerifyDomain,
	useVerifyDomainCode,
} from '@repo/api-codegen';
import {
	SettingsBlock,
	SettingsButton,
	SettingsSelect,
	SettingsSwitch,
} from '@repo/common/components';
import { UserRole } from '@repo/common/enums/UserRole.ts';
import {
	Banner,
	Button,
	IconButton,
	ResourceIcon,
	Text,
	Title,
} from '@repo/foundations';
import { debounce } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import type { IWorkspace } from '../../api';
import { useAuthUser } from '../../api';
import { useLegacySaml } from '../../api/hooks/saml';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import { useUpdateWorkspace } from '../../api/hooks/workspace/useUpdateWorkspace';
import { useWorkspacePermissionV2 } from '../../api/hooks/workspace/useWorkspacePermissionV2';
import { SSO_NAMES } from '../Auth/utils';
import { EmptyState } from '../EmptyState';
import { openModal } from '../ModalManager';
import { closeAllModals, openConfirmModal } from '../ModalManager/events';
import { SamlSettingsForm } from './SamlSettingsForm';
import { UpgradeButton } from './UpgradeButton';

const SSO_OPTIONS = [
	{ value: 'none', label: SSO_NAMES.none },
	{ value: 'google', label: SSO_NAMES.google },
	{ value: 'microsoft', label: SSO_NAMES.microsoft },
	{ value: 'saml', label: SSO_NAMES.saml },
];

interface DomainCardProps {
	domain: string;
	onDelete: (domain: string) => void;
}

const id = 'add-domain-modal';

function DomainCard({ domain, onDelete }: DomainCardProps) {
	const handleDelete = useCallback(() => {
		onDelete(domain);
	}, [domain, onDelete]);

	return (
		<SettingsBlock
			leftComponent={<ResourceIcon iconName="at" />}
			title={
				<Text size="sm" weight="semibold">
					{domain}
				</Text>
			}
			rightComponentHover={
				<IconButton
					iconName="x"
					onClick={handleDelete}
					variant="tertiary"
					tooltip="Remove domain"
				/>
			}
		/>
	);
}

function AddDomainModal({
	onClose,
	onSubmit,
}: {
	onClose: () => void;
	onSubmit: (domain: string) => void;
}) {
	const [step, setStep] = useState<'domain' | 'email' | 'verification'>(
		'domain'
	);
	const [domain, setDomain] = useState('');
	const [email, setEmail] = useState('');
	const [code, setCode] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const { mutateAsync: verifyDomain } = useVerifyDomain();
	const { mutateAsync: verifyDomainCode } = useVerifyDomainCode();

	const handleDomainNext = useCallback(() => {
		if (domain && /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/.test(domain)) {
			setStep('email');
		} else {
			setError('Invalid domain');
		}
	}, [domain]);

	const handleSendVerification = useCallback(async () => {
		if (!email) return;

		// Check if email domain matches the entered domain
		const emailDomain = email.split('@')[1];
		if (emailDomain !== domain) {
			setError(`Email must be from the domain ${domain}`);
			return;
		}

		setIsLoading(true);
		setError(null);

		try {
			await verifyDomain({
				body: {
					domain,
					email,
				},
			});

			setStep('verification');
			showNotification({
				title: 'Verification code sent',
				message: 'Please check your email for the verification code',
				color: 'green',
			});
		} catch (err: unknown) {
			setError(
				(err as { message: string }).message ||
					'Failed to send verification code'
			);
		} finally {
			setIsLoading(false);
		}
	}, [domain, email, verifyDomain]);

	const handleVerifyCode = useCallback(async () => {
		if (!code) return;

		setIsLoading(true);
		setError(null);

		try {
			await verifyDomainCode({
				body: {
					domain,
					email,
					code,
				},
			});

			showNotification({
				title: 'Domain verified',
				message: 'Domain has been verified and added successfully',
				color: 'green',
			});

			onSubmit(domain);
			onClose();
		} catch (err: unknown) {
			setError(
				(err as { message: string }).message || 'Failed to verify domain'
			);
		} finally {
			setIsLoading(false);
		}
	}, [domain, email, code, onSubmit, onClose, verifyDomainCode]);

	const handleBackToDomain = useCallback(() => {
		setStep('domain');
		setError(null);
	}, []);

	const handleBackToEmail = useCallback(() => {
		setStep('email');
		setError(null);
	}, []);

	if (step === 'domain') {
		return (
			<Stack spacing="0">
				<Text size="sm" weight="semibold">
					Email domain
				</Text>
				<Input
					name="domain"
					placeholder="example.com"
					value={domain}
					onChange={(e) => setDomain(e.currentTarget.value)}
					data-autofocus
				/>
				<Text size="sm" color="text/secondary/default" mb="sm">
					Users from this domain can join this workspace
				</Text>
				<Divider mb="md" />
				{error && <Text color="text/critical/default">{error}</Text>}
				<Group position="right">
					<Button onClick={onClose} variant="default">
						Cancel
					</Button>
					<Button
						onClick={handleDomainNext}
						variant="primary"
						disabled={!domain}
					>
						Add domain
					</Button>
				</Group>
			</Stack>
		);
	}

	if (step === 'email') {
		return (
			<Stack spacing="sm">
				<Text size="sm" color="text/secondary/default">
					Enter an email address from the domain "{domain}" to receive a
					verification code.
				</Text>
				<Input
					name="email"
					placeholder={`user@${domain}`}
					value={email}
					onChange={(e) => setEmail(e.currentTarget.value)}
					disabled={isLoading}
				/>
				{error && <Text color="text/critical/default">{error}</Text>}
				<Group position="right">
					<Button
						onClick={handleBackToDomain}
						variant="tertiary"
						disabled={isLoading}
					>
						Back
					</Button>
					<Button
						onClick={handleSendVerification}
						variant="primary"
						disabled={!email || isLoading}
						loading={isLoading}
					>
						Send verification
					</Button>
				</Group>
			</Stack>
		);
	}

	return (
		<Stack spacing="sm">
			<Text size="sm" color="text/secondary/default">
				Enter the verification code sent to {email}
			</Text>
			<Input
				name="code"
				placeholder="Enter verification code"
				value={code}
				onChange={(e) => setCode(e.currentTarget.value)}
				disabled={isLoading}
			/>
			{error && <Text color="text/critical/default">{error}</Text>}
			<Group position="right">
				<Button
					onClick={handleBackToEmail}
					variant="tertiary"
					disabled={isLoading}
				>
					Back
				</Button>
				<Button
					onClick={handleVerifyCode}
					variant="primary"
					disabled={!code || isLoading}
					loading={isLoading}
				>
					Verify domain
				</Button>
			</Group>
			<Text size="xs" color="text/secondary/default" align="right">
				<Button
					onClick={handleSendVerification}
					variant="tertiary"
					size="sm"
					disabled={isLoading}
				>
					Resend code
				</Button>
			</Text>
		</Stack>
	);
}

export function SecuritySettings() {
	const { isAdminUser, workspace } = useAuthUser();
	const disabled = !isAdminUser;
	const { mutateAsync } = useUpdateWorkspace(workspace!.id);

	const { data: samlData } = useLegacySaml();

	const { samlAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const migratedPermissionV2 = useWorkspacePermissionV2();
	const { data: iamRoles } = useApiListIamRoles({});

	const { data: allowedDomains, refetch } = useApiListAllowedDomains({});
	const { mutateAsync: deleteDomain } = useApiDeleteAllowedDomain({
		onSuccess: () => {
			refetch();
		},
	});

	const debouncedUpdate = useMemo(
		() =>
			debounce((values: Partial<IWorkspace>) => {
				mutateAsync({
					data: {
						id: workspace?.id,
						...values,
					},
				}).then(() => {
					showNotification({
						title: 'Workspace updated',
						message: 'Workspace information updated successfully',
						color: 'green',
						autoClose: 1500,
					});
				});
			}, 500),
		[mutateAsync, workspace?.id]
	);

	const handleScimRoleChange = useCallback(
		(role: UserRole | string) => {
			debouncedUpdate({ default_scim_role: role });
		},
		[debouncedUpdate]
	);

	const handleEnforceScimOnlyProvisioningChange = useCallback(
		(checked: boolean) => {
			debouncedUpdate({ enforce_scim_only_provisioning: checked });
		},
		[debouncedUpdate]
	);

	const showAddDomainModal = useCallback(() => {
		openModal({
			id,
			title: 'Add email domain',
			children: (
				<AddDomainModal onClose={closeAllModals} onSubmit={() => refetch()} />
			),
		});
	}, [refetch]);

	return (
		<Box>
			{secodaCoreAccess && (
				<>
					<Title size="md" mb="md">
						General
					</Title>
					<Box mb="4xl">
						<SettingsSwitch
							disabled={disabled}
							onChange={(checked) => {
								debouncedUpdate({ lax_cookie_security: checked });
							}}
							checked={workspace.lax_cookie_security ?? false}
							title="Allow embedding"
							description="Allow your Secoda workspace to be embedded in an iframe. Note: This may increase the risk of CSRF attacks"
						/>
					</Box>
				</>
			)}

			<Title size="md">Allowed email domains</Title>
			<Text size="sm" color="text/secondary/default" mb="md">
				Workspace members must belong to one of these domains
			</Text>
			<Box mb="4xl">
				<SettingsButton
					iconName="plus"
					variant="tertiary"
					title="Allowed domains"
					description="Users from these domains can join this workspace"
					onClick={showAddDomainModal}
					tooltip="Add domain"
				/>
				{(allowedDomains?.allowed_domains || []).map((domain: string) => (
					<DomainCard
						key={domain}
						domain={domain}
						onDelete={() =>
							deleteDomain({
								body: {
									domain,
								},
							})
						}
					/>
				))}
			</Box>

			<Title size="md" mb="md">
				Authentication
			</Title>
			<Box mb="4xl">
				<SettingsSelect
					title="Enforce SSO"
					description="Require users to sign in with a specific SSO provider"
					value={workspace.enforce_sso ?? 'none'}
					options={SSO_OPTIONS.filter((option) => {
						if (option.value === 'saml') {
							return !!samlAccess;
						}
						return true;
					})}
					onChange={(value) => {
						if (value === 'none') {
							debouncedUpdate({ enforce_sso: null });
						} else {
							openConfirmModal({
								title: 'Enforce SSO Authentication',
								children: (
									<Text size="sm">
										Warning: Enforcing SSO authentication may lock you out if
										your SSO provider is not configured correctly. Please test
										the SSO login before enforcing it. You can reset this by
										contacting Secoda support.
									</Text>
								),
								confirmLabel: 'Enforce SSO',
								cancelLabel: 'Cancel',
								onConfirm: () => {
									debouncedUpdate({ enforce_sso: value });
								},
							});
						}
					}}
				/>
			</Box>

			{secodaCoreAccess && (
				<>
					<Title size="md" mb="md">
						SAML authentication
					</Title>
					<Box mb="4xl">
						{!samlAccess ? (
							<EmptyState
								title="Upgrade to access SAML & SCIM"
								description="You can use an SAML & SCIM to programatically provision users in Secoda"
								illustrationName="upgrade"
								includeGoBack={false}
								stateHeight="40vh"
								size="lg"
								withActions={<UpgradeButton feature="SAML & SCIM" size="md" />}
							/>
						) : (
							<Box>
								{/* A message to prevent "legacy" SAML configurations from being overwritten. This banner will NOT display on self-serve /saml/v2/ configurations since they are fully self-serve */}
								{samlData?.METADATA_AUTO_CONF_URL ? (
									<Box mb="md">
										<Banner
											tone="success"
											title="SAML configuration verified"
											message="Your SAML IdP configuration is managed by Secoda"
										/>
									</Box>
								) : (
									<Box>
										<SamlSettingsForm />
									</Box>
								)}
								<Box mt="xl">
									<SettingsSelect
										title="SCIM default role"
										description="The default role assigned to users provisioned via SCIM"
										value={workspace.default_scim_role ?? UserRole.VIEWER}
										options={useMemo(() => {
											const legacyOptions = [
												{ value: UserRole.VIEWER, label: 'Viewer' },
												{ value: UserRole.EDITOR, label: 'Editor' },
												{ value: UserRole.ADMIN, label: 'Admin' },
											];

											// Add custom roles if permission v2 is enabled
											if (migratedPermissionV2 && iamRoles?.results) {
												const customRoles = iamRoles.results
													.filter((role) => !role.is_legacy)
													.map((role) => ({
														value: `custom.${role.name}`,
														label: role.name,
													}));

												return [...legacyOptions, ...customRoles];
											}

											return legacyOptions;
										}, [migratedPermissionV2, iamRoles])}
										onChange={(value) => {
											handleScimRoleChange(value ?? UserRole.VIEWER);
										}}
									/>
									<SettingsSwitch
										title="SCIM-only user provisioning"
										description="Prevents users from being invited or disabled in Secoda"
										checked={workspace.enforce_scim_only_provisioning ?? false}
										onChange={handleEnforceScimOnlyProvisioningChange}
									/>
								</Box>
							</Box>
						)}
					</Box>
				</>
			)}
		</Box>
	);
}
